import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { useSelector, useDispatch } from 'react-redux';
import { Icon } from 'src/skins/common/icon';
import { VenusButton } from 'src/skins/venus/components/common';
import { DashboardLayout } from 'src/skins/venus/components/layouts';
import UserIcon from 'src/assets/svgs/icon-user.svg';
import { infoAlert } from 'src/store/actions/alertActions';
import { copyToClipboard } from 'src/utils';
import { createVirtualWallet, getVirtualWalletRequests } from 'src/store/actions/userActions';
import { RequestBvnModal } from 'src/skins/venus/modals';

const pageTitle = 'Fund wallet';

const Wrapper = styled.div`
  color: ${props => props.theme.color.venus.text};

  .header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 0.5rem;
    img {
      width: 1.5rem;
      height: 1.5rem;
    }
  }
  section {
    margin: 1rem 0;
  }
  .flex-between {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 0.5rem;
    gap: 0.5rem;
  }

  .transfer-instructions {
    margin-top: 2rem;
    margin-bottom: 1rem;
    box-shadow: 0 2px 4px 0 rgba(0, 0, 0, 0.4);
    padding: 1rem;
    border-radius: 8px;
    background-color: ${props => props.theme.color.primary};
    color: ${props => props.theme.color.light};

    .transfer-to {
      font-weight: 600;
    }

    .account-info {
      position: relative;
      font-size: 1rem;
      text-align: center;

      .copy-wrapper {
        position: absolute;
        top: 0;
        right: 0;
        display: flex;
        justify-content: flex-end;
        text-align: right;
        width: fit-content;
        cursor: pointer;
      }

      & > div {
        & > :first-child {
          font-weight: 500;
        }
      }
    }
  }

  .account-names {
    display: flex;
    justify-content: space-between;
    margin: 1rem 0 2rem 0;
    font-size: 1rem;
  }

  .data-flex {
    display: flex;
    justify-content: space-between;

    & > * {
      margin: 0.25rem 0;

      &::first-child-child {
        margin-right: 0.5rem;
      }

      &:last-child {
        font-weight: 600;
      }
    }
  }
`;

const Wallet = () => {
  const mmo = useSelector(state => state.MMO.mmo);
  const { wallet, isLoading, virtualWalletRequests } = useSelector(state => state.USER);
  const unapprovedWalletRequests = virtualWalletRequests.filter(request => {
    return request.status !== 'approved';
  });
  const dispatch = useDispatch();
  const copiedAlert = value => dispatch(infoAlert('Copied: ' + value));

  useEffect(() => {
    dispatch(getVirtualWalletRequests());
  }, []);

  const virtualWalletsHaveBankName = bankName => {
    const virtualWallets = wallet?.virtualWallets || [];
    const hasBankName = virtualWallets.some(wallet => {
      return new RegExp(bankName, 'i').test(wallet.bankName);
    });
    return hasBankName;
  };

  const mmoIsNotBlacklisted = !mmo.blacklisted;
  const mmoIsKredi = mmo?.shortCode === 'kredi';
  const mmoIsNotKredi = !mmoIsKredi;
  const safeHavenWalletExist = () => virtualWalletsHaveBankName('SAFE HAVEN');
  const krediWalletExist = () => virtualWalletsHaveBankName('Kredi');
  const providusWalletExist = () => virtualWalletsHaveBankName('Providus');
  const transferToMsg =
    wallet?.virtualWallets?.length > 1
      ? 'Transfer to any of these accounts to fund your wallet'
      : 'Transfer to this account to fund your wallet';

  const [showBvnModal, setShowBvnModal] = useState(false);
  const openBvnModal = vendor => setShowBvnModal(vendor);
  const closeBvnModal = () => setShowBvnModal(false);

  const virtualWallets = mmoIsKredi
    ? (wallet.virtualWallets || []).filter(wallet => {
        return new RegExp('kredi', 'i').test(wallet.bankName);
      })
    : wallet.virtualWallets || [];

  return (
    <DashboardLayout>
      <Wrapper>
        <div className="header">
          <h2>{pageTitle}</h2>
          <img src={UserIcon} alt="USER" />
        </div>

        <br />

        <div className="text-center">{transferToMsg}</div>

        <section>
          {isLoading.getUserWallet && <p className="text-center">Fetching Wallet details ....</p>}
          {isLoading.createVirtualWallet && <p className="text-center">Creating virtual wallet</p>}

          <br />

          {!isLoading.state &&
            virtualWallets.length > 0 &&
            virtualWallets.map((wallet, index) => (
              <div key={wallet.accountNumber}>
                <input
                  id={`virtualAcctNumber${index}`}
                  type="text"
                  value={wallet.accountNumber}
                  onChange={null}
                  readOnly
                  style={hiddenInputStyle}
                />
                <div className="transfer-instructions">
                  <div className="account-info">
                    <div className="copy-wrapper">
                      <Icon
                        name="copy"
                        size="small"
                        onClick={() => copyToClipboard(`virtualAcctNumber${index}`, copiedAlert)}
                      />
                    </div>
                    <div>
                      <div className="text-xl">{wallet.accountNumber}</div>
                      <div className="text-sm">Account Number</div>
                    </div>
                  </div>
                </div>
                <div className="account-names">
                  <div>
                    <div>{wallet.accountName}</div>
                    <div className="text-xs">Account Name</div>
                  </div>
                  <div className="text-right">
                    <div>{wallet.bankName}</div>
                    <div className="text-xs">Bank Name</div>
                  </div>
                </div>
              </div>
            ))}

          {mmoIsNotBlacklisted && mmoIsNotKredi && (
            <>
              {!safeHavenWalletExist() && (
                <>
                  <VenusButton
                    size="small"
                    text="Generate Safe Haven Virtual Account"
                    isLoading={isLoading.createVirtualWallet === 'safe-haven'}
                    onClick={() => openBvnModal('safe-haven')}
                  />
                  <RequestBvnModal
                    isOpen={showBvnModal === 'safe-haven'}
                    closeModal={closeBvnModal}
                    clickIsLoading={isLoading.createVirtualWallet === 'safe-haven'}
                    handleConfirmTransactionClick={bvn => {
                      dispatch(
                        createVirtualWallet(
                          { bvn, vendor: 'safe-haven' },
                          {
                            success: () => closeBvnModal(),
                          }
                        )
                      );
                    }}
                  />
                </>
              )}

              <br />
            </>
          )}

          {!krediWalletExist() && (
            <>
              <VenusButton
                size="small"
                text="Generate Kredi Virtual Account"
                isLoading={isLoading.createVirtualWallet === 'kredi'}
                onClick={() => openBvnModal('kredi')}
              />
              <RequestBvnModal
                isOpen={showBvnModal === 'kredi'}
                closeModal={closeBvnModal}
                clickIsLoading={isLoading.createVirtualWallet === 'kredi'}
                handleConfirmTransactionClick={bvn => {
                  dispatch(
                    createVirtualWallet(
                      { bvn, vendor: 'kredi' },
                      {
                        success: () => closeBvnModal(),
                      }
                    )
                  );
                }}
              />
              <br />
            </>
          )}

          {mmoIsNotBlacklisted && mmoIsNotKredi && (
            <>
              {!providusWalletExist() && (
                <>
                  <VenusButton
                    size="small"
                    text="Generate Providus Virtual Account"
                    isLoading={isLoading.createVirtualWallet === 'providus'}
                    onClick={() => openBvnModal('providus')}
                  />
                  <RequestBvnModal
                    isOpen={showBvnModal === 'providus'}
                    closeModal={closeBvnModal}
                    clickIsLoading={isLoading.createVirtualWallet === 'providus'}
                    handleConfirmTransactionClick={bvn => {
                      dispatch(
                        createVirtualWallet(
                          { bvn, vendor: 'providus' },
                          {
                            success: () => closeBvnModal(),
                          }
                        )
                      );
                    }}
                  />
                </>
              )}
            </>
          )}
        </section>

        {unapprovedWalletRequests.length > 0 && (
          <section>
            <br />

            <div className="header">
              <h3>Wallet Requests</h3>
            </div>

            <div>
              {unapprovedWalletRequests.map((request, index) => (
                <div key={index}>
                  <div className="flex-between">
                    <span>{index + 1}.</span>
                  </div>
                  <div className="flex-between">
                    <span>Virtual Wallet Type</span>
                    <span>{request.vendor}</span>
                  </div>
                  <div className="flex-between">
                    <span>Status</span>
                    <span>{request.status}</span>
                  </div>
                  {request.status !== 'pending' && request.reason && (
                    <div className="flex-between">
                      <span>Reason</span>
                      <span>{request.reason}</span>
                    </div>
                  )}
                  <div className="flex-between">
                    <span>Requested on</span>
                    <span>{request.timeCreated ? new Date(request.timeCreated).toDateString() : ''}</span>
                  </div>
                </div>
              ))}
            </div>
          </section>
        )}
      </Wrapper>
    </DashboardLayout>
  );
};

const hiddenInputStyle = { opacity: 0, display: 'block', marginBottom: '-5rem' };

export default Wallet;
